import { CartDetails } from "../../lib/cart/CartDetails";
import EditPenSvg from "../../svg/pen.svg";
import ErrorsList from "../ErrorsList";
import FontFamilySection from "./FontFamilySection";
import FoundryLink from "../../components/FoundryLink";
import FoundryPhysicalProduct from "../../lib/foundry_client/models/FoundryPhysicalProduct";
import Loader from "../Loader";
import PhysicalProductSection from "./PhysicalProductSection";
import { PhysicalProductsContext } from "../../contexts/PhysicalProductsContext";
import Routes from "../../routes";
import Separator from "../Separator";
import { useContext } from "react";

interface Props {
  cart: CartDetails;
}

const FontsLicensing = (cart: CartDetails) => {
  const hasFontProducts = Object.entries(cart.families)
    .map(([familyName, familyDetails]) => {
      if (familyDetails.items.length === 0) {
        return null;
      }
      return familyName;
    })
    .filter((name) => name);

  if (hasFontProducts.length === 0) {
    return null;
  }

  return (
    <>
      <h2 className="t5">Fonts Licensing</h2>
      <Separator classes="pb-4" hideLeft={true} />
      <div className="bg-neutral-500 p-6 mb-8 rounded">
        <div className="flex justify-between">
          <h2 className="t7 mb-2">License Holder Information</h2>
          <FoundryLink
            href={Routes.fonts.add.licenseHolder(hasFontProducts[0])}
            className="w-5 h-5"
          >
            <EditPenSvg className="cursor-pointer color-blue-hover" />
          </FoundryLink>
        </div>
        <div className="flex flex-col gap-0">
          <span>Name: {cart.licenseHolder?.name}</span>
          <span>Email Address: {cart.licenseHolder?.emailAddress}</span>
          <span>Company Size: {cart.companySizeString()}</span>
        </div>
      </div>
      {Object.entries(cart.families).map(([familyName, familyDetails]) => {
        if (familyDetails.items.length === 0) {
          return;
        }

        return (
          <>
            <FontFamilySection
              familyName={familyName}
              familyDetails={familyDetails}
              cartDetails={cart}
            />
            <hr className="mt-5 mb-6 w-full flex-row items-center justify-center" />
          </>
        );
      })}
    </>
  );
};

const PhysicalProducts = (
  cart: CartDetails,
  physicalProducts: FoundryPhysicalProduct[]
) => {
  const hasPhysicalProducts =
    cart.physicalProducts &&
    cart.physicalProducts
      .map((product) => {
        const productDetails = physicalProducts.find(
          (p) => p.id === product.contentfulId
        );

        return productDetails != undefined;
      })
      .filter((isFound) => isFound).length > 0;

  if (!hasPhysicalProducts) {
    return null;
  }

  const productDiscounts = cart.physicalProductDiscounts(physicalProducts);

  return (
    <>
      <h2 className="t5">Physical Products</h2>
      <ul className="nostyle">
        <li className="mb-1">
          Products are available to ship within the United States.
        </li>
        <li>
          We ship orders twice a week on Mondays and Thursdays; please allow for
          additional shipping time based on your order date.
        </li>
      </ul>
      <p className="pb-1">
        See our{" "}
        <FoundryLink
          href={Routes.faq}
          className="underline underline-offset-4 color-blue-hover"
        >
          shipping and return policies
        </FoundryLink>{" "}
        for more information.
      </p>
      <Separator classes="pb-4" hideLeft={true} />
      {cart.physicalProducts.map((product) => {
        const productDetails = physicalProducts?.find(
          (p) => p.id === product.contentfulId
        );
        if (!productDetails) {
          return;
        }

        return (
          <>
            <PhysicalProductSection
              product={product}
              productDetails={productDetails}
              discountCents={productDiscounts[product.contentfulId]}
              key={product.contentfulId}
            />
            <hr className="my-6 w-full flex-row items-center justify-center" />
          </>
        );
      })}
    </>
  );
};

export default function ReviewCart(props: Props) {
  const { physicalProducts } = useContext(PhysicalProductsContext);
  // TODO: This shows when physical products are still being loaded sometimes.
  const errors =
    props.cart.getInvalidPhysicalProductQuantities(physicalProducts);

  if (physicalProducts.length === 0) {
    return <Loader size={90} />;
  }
  return (
    <>
      {errors.length > 0 && (
        <div className="mb-8">
          <ErrorsList title="There are issues in your cart." errors={errors} />
        </div>
      )}
      {FontsLicensing(props.cart)}
      {PhysicalProducts(props.cart, physicalProducts)}
    </>
  );
}
